<script setup lang="ts">
import JSZip from 'jszip'

import { storeToRefs } from 'pinia'
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import PluginIcon from '../../../../common/PluginIcon.vue'

import { useContactsStore } from '../../../../../stores/contacts'
import { useAiriCardStore } from '../../../../../stores/modules/airi-card'
import { usePluginsStore } from '../../../../../stores/plugins'
import { showError, showSuccess } from '../../../../../utils/toast'
import { Button } from '../../../../misc'

interface Emits {
  (e: 'next'): void
  (e: 'skip'): void
}

const emit = defineEmits<Emits>()

const { t } = useI18n()
const contentReady = inject<import('vue').Ref<boolean>>('onboarding-content-ready', ref(true))
const pluginsStore = usePluginsStore()
const airiCardStore = useAiriCardStore()
const contactsStore = useContactsStore()
const { installedPlugins } = storeToRefs(pluginsStore)

const BASE = import.meta.env.DEV ? '/api/ground' : 'https://ground.nktp.top'
const PAGE_SIZE = 12

// ── 广场数据 ──
const marketplaceTab = ref<'plugins' | 'personas'>('personas')
const pluginList = ref<any[]>([])
const personaList = ref<any[]>([])
const loading = ref(false)
const loadMoreLoading = ref(false)
const loadError = ref(false)
const installingId = ref<string | null>(null)
const importedPersonaIdMap = ref<Record<string, boolean>>({})

const searchQuery = ref('')
const sortBy = ref<'downloads' | 'date'>('downloads')
const pluginPage = ref(1)
const personaPage = ref(1)
const pluginTotalPages = ref(0)
const personaTotalPages = ref(0)

function buildQuery(page: number) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(PAGE_SIZE),
    sort: sortBy.value,
    order: 'desc',
  })
  if (searchQuery.value.trim())
    params.set('search', searchQuery.value.trim())
  return params.toString()
}

async function fetchList(reset = true) {
  if (reset) {
    pluginPage.value = 1
    personaPage.value = 1
    pluginList.value = []
    personaList.value = []
  }
  loading.value = true
  loadError.value = false
  try {
    if (marketplaceTab.value === 'plugins') {
      const res = await fetch(`${BASE}/api/items/plugins?${buildQuery(pluginPage.value)}`)
      if (res.ok) {
        const d = await res.json()
        pluginList.value = d.items ?? d
        pluginTotalPages.value = d.totalPages ?? 1
      }
    }
    else {
      const res = await fetch(`${BASE}/api/items/personas?${buildQuery(personaPage.value)}`)
      if (res.ok) {
        const d = await res.json()
        personaList.value = d.items ?? d
        personaTotalPages.value = d.totalPages ?? 1
      }
    }
  }
  catch {
    loadError.value = true
  }
  finally {
    loading.value = false
  }
}

async function loadMore() {
  if (loading.value)
    return
  const isPlugin = marketplaceTab.value === 'plugins'
  const page = isPlugin ? pluginPage.value : personaPage.value
  const totalPages = isPlugin ? pluginTotalPages.value : personaTotalPages.value
  if (page >= totalPages)
    return
  loadMoreLoading.value = true
  try {
    const nextPage = page + 1
    const res = await fetch(`${BASE}/api/items/${isPlugin ? 'plugins' : 'personas'}?${buildQuery(nextPage)}`)
    if (res.ok) {
      const d = await res.json()
      const items = d.items ?? d
      if (isPlugin) {
        pluginList.value.push(...items)
        pluginPage.value = nextPage
        pluginTotalPages.value = d.totalPages ?? pluginTotalPages.value
      }
      else {
        personaList.value.push(...items)
        personaPage.value = nextPage
        personaTotalPages.value = d.totalPages ?? personaTotalPages.value
      }
    }
  }
  finally {
    loadMoreLoading.value = false
  }
}

const hasMore = computed(() => {
  if (marketplaceTab.value === 'plugins')
    return pluginPage.value < pluginTotalPages.value
  return personaPage.value < personaTotalPages.value
})

let searchTimer: ReturnType<typeof setTimeout> | null = null
function onSearchInput() {
  if (searchTimer)
    clearTimeout(searchTimer)
  searchTimer = setTimeout(() => fetchList(true), 300)
}

function switchTab(tab: 'plugins' | 'personas') {
  marketplaceTab.value = tab
  fetchList(true)
}

const installedBySourceUrl = computed(() => {
  const map = new Map<string, (typeof installedPlugins.value)[0]>()
  for (const p of installedPlugins.value) {
    if (p.sourceUrl)
      map.set(p.sourceUrl, p)
  }
  return map
})

const pluginListWithStatus = computed(() =>
  pluginList.value.map((item) => {
    const downloadUrl = `${BASE}${item.downloadUrl}`
    const byManifestId = installedPlugins.value.find(
      p => p.plugin.metadata.id === (item.manifestId || item.id),
    )
    const bySourceUrl = installedBySourceUrl.value.get(downloadUrl)
    const match = byManifestId || bySourceUrl
    const installed = !!match
    const hasUpdate = installed && match!.plugin.metadata.version !== item.version
    return { ...item, installed, hasUpdate }
  }),
)

const currentList = computed(() => {
  if (marketplaceTab.value === 'plugins')
    return pluginListWithStatus.value
  return personaList.value
})

async function installPlugin(item: any) {
  if (installingId.value)
    return
  installingId.value = item.id
  try {
    await pluginsStore.installPluginFromUrl(`${BASE}${item.downloadUrl}`)
    await fetch(`${BASE}/api/download/plugins/${item.id}`, { method: 'POST' })
    const original = pluginList.value.find(p => p.id === item.id)
    if (original)
      original.downloads = (original.downloads || 0) + 1
    showSuccess(t('settings.dialogs.onboarding.marketplace.installSuccess', { name: item.name }))
  }
  catch (e: any) {
    showError(t('settings.dialogs.onboarding.marketplace.installError', { msg: e?.message || t('settings.dialogs.onboarding.marketplace.unknownError') }))
  }
  finally {
    installingId.value = null
  }
}

async function installPersona(item: any) {
  if (installingId.value)
    return
  installingId.value = item.id
  try {
    const res = await fetch(`${BASE}${item.downloadUrl}`)
    if (!res.ok)
      throw new Error(t('settings.dialogs.onboarding.marketplace.downloadError'))
    const zip = await JSZip.loadAsync(await res.arrayBuffer())
    const dataFile = zip.file('data.json')
    if (!dataFile)
      throw new Error(t('settings.dialogs.onboarding.marketplace.zipMissingData'))
    const data = JSON.parse(await dataFile.async('string'))

    await airiCardStore.waitForLoad()
    await contactsStore.waitForLoad()

    const payload = data.data || data
    const rawCards: any[] = Array.isArray(payload.cards)
      ? payload.cards
      : Object.values(payload.cards || {})
    const contactList: any[] = payload.contacts || []

    const cardNameToNewId = new Map<string, string>()
    for (const card of rawCards) {
      const newId = airiCardStore.addCard(card)
      if (card.name)
        cardNameToNewId.set(card.name, newId)
    }

    for (const contact of contactList) {
      if (contact.isGroup) {
        contactsStore.createContact({
          name: contact.name,
          cardId: '',
          isGroup: true,
          members: contact.members || [],
          avatar: contact.avatar,
          metadata: contact.metadata,
        })
        continue
      }
      const newCardId = cardNameToNewId.get(contact.name)
        ?? (cardNameToNewId.size === 1 ? [...cardNameToNewId.values()][0] : contact.cardId)
      contactsStore.createContact({
        name: contact.name,
        cardId: newCardId,
        avatar: contact.avatar,
        groupId: contact.groupId,
        displayModelId: contact.displayModelId,
        worldBookIds: contact.worldBookIds,
        stickerPackIds: contact.stickerPackIds,
        boundPersonaId: contact.boundPersonaId,
        locationType: contact.locationType,
        offlineModeEnabled: contact.offlineModeEnabled,
        isGroup: false,
        members: [],
        metadata: contact.metadata,
      })
    }

    await fetch(`${BASE}/api/download/personas/${item.id}`, { method: 'POST' })
    item.downloads = (item.downloads || 0) + 1
    importedPersonaIdMap.value[item.id] = true
    localStorage.setItem('marketplace-imported-personas', JSON.stringify(importedPersonaIdMap.value))
    showSuccess(t('settings.dialogs.onboarding.marketplace.importSuccess', { name: item.name }))
  }
  catch (e: any) {
    showError(t('settings.dialogs.onboarding.marketplace.importError', { msg: e?.message || t('settings.dialogs.onboarding.marketplace.unknownError') }))
  }
  finally {
    installingId.value = null
  }
}

onMounted(() => {
  importedPersonaIdMap.value = JSON.parse(localStorage.getItem('marketplace-imported-personas') || '{}')
  fetchList()
})

onUnmounted(() => {
  if (searchTimer)
    clearTimeout(searchTimer)
})
</script>

<template>
  <div class="min-h-0 flex flex-1 flex-col gap-3">
    <template v-if="contentReady">
      <div
        v-motion
        :initial="{ opacity: 0, y: 20 }"
        :enter="{ opacity: 1, y: 0 }"
        :duration="420"
        class="flex-shrink-0"
      >
        <h2 class="text-xl text-neutral-800 md:text-2xl dark:text-neutral-100">
          {{ t('settings.dialogs.onboarding.marketplace.title') }}
        </h2>
        <p class="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {{ t('settings.dialogs.onboarding.marketplace.description') }}
        </p>
      </div>

      <div
        v-motion
        :initial="{ opacity: 0, y: 16 }"
        :enter="{ opacity: 1, y: 0 }"
        :duration="380"
        :delay="120"
        class="flex-1 overflow-y-auto space-y-3"
      >
        <!-- Tab -->
        <div class="flex gap-2">
          <button
            class="rounded-full px-3 py-1 text-sm transition-colors"
            :class="marketplaceTab === 'personas' ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'text-neutral-500 dark:text-neutral-400'"
            @click="switchTab('personas')"
          >
            {{ t('settings.dialogs.onboarding.marketplace.personaTab') }}
          </button>
          <button
            class="rounded-full px-3 py-1 text-sm transition-colors"
            :class="marketplaceTab === 'plugins' ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'text-neutral-500 dark:text-neutral-400'"
            @click="switchTab('plugins')"
          >
            {{ t('settings.dialogs.onboarding.marketplace.pluginTab') }}
          </button>
        </div>

        <!-- 搜索 -->
        <div class="flex items-center gap-2 border border-neutral-200/60 rounded-xl bg-neutral-50 px-3 py-1.5 dark:border-neutral-700/60 dark:bg-neutral-800/60">
          <div class="i-solar:magnifer-linear flex-shrink-0 text-neutral-400" />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="marketplaceTab === 'plugins' ? t('settings.dialogs.onboarding.marketplace.searchPlugins') : t('settings.dialogs.onboarding.marketplace.searchPersonas')"
            class="flex-1 bg-transparent text-sm text-neutral-800 outline-none dark:text-neutral-100"
            @input="onSearchInput"
          >
          <button v-if="searchQuery" class="i-solar:close-circle-linear text-neutral-400" @click="searchQuery = ''; fetchList(true)" />
        </div>

        <!-- 加载中 -->
        <div v-if="loading" class="flex flex-col gap-2 pt-2">
          <div v-for="i in 3" :key="i" class="h-16 animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-700" />
        </div>

        <!-- 加载失败 -->
        <div v-else-if="loadError" class="rounded-lg bg-neutral-50 px-4 py-4 text-center dark:bg-neutral-800">
          <p class="text-sm text-neutral-500 dark:text-neutral-400">
            {{ t('settings.dialogs.onboarding.marketplace.loadError') }}
          </p>
          <button class="mt-2 text-sm text-primary-600 underline decoration-dashed dark:text-primary-400" @click="fetchList(true)">
            {{ t('settings.dialogs.onboarding.marketplace.retry') }}
          </button>
        </div>

        <!-- 空状态 -->
        <div v-else-if="currentList.length === 0" class="py-4 text-center text-sm text-neutral-400">
          {{ searchQuery ? t('settings.dialogs.onboarding.marketplace.noSearchResult', { query: searchQuery }) : t('settings.dialogs.onboarding.marketplace.noContent') }}
        </div>

        <!-- 列表 -->
        <div v-else class="space-y-2">
          <div
            v-for="item in currentList" :key="item.id"
            class="flex items-center gap-3 border border-neutral-200/60 rounded-xl bg-white/80 p-2.5 dark:border-neutral-700/60 dark:bg-neutral-800/80"
          >
            <!-- 图标 -->
            <div class="flex-shrink-0">
              <PluginIcon
                v-if="item.icon?.startsWith('i-')"
                :icon="item.icon"
                class="h-10 w-10 flex items-center justify-center text-2xl"
              />
              <img
                v-else-if="item.icon"
                :src="`${BASE}${item.icon}`"
                class="h-10 w-10 rounded-xl object-cover"
              >
              <div v-else class="i-solar:widget-add-bold-duotone h-10 w-10 flex items-center justify-center text-2xl text-neutral-400" />
            </div>
            <!-- 信息 -->
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1">
                <p class="flex-1 truncate text-sm text-neutral-800 font-medium dark:text-neutral-100">
                  {{ item.name }}
                </p>
                <span class="flex flex-shrink-0 items-center gap-0.5 text-xs text-neutral-400">
                  <span class="i-solar:download-minimalistic-linear text-xs" />
                  {{ item.downloads || 0 }}
                </span>
              </div>
              <p class="truncate text-xs text-neutral-500 dark:text-neutral-400">
                {{ item.description }}
              </p>
            </div>
            <!-- 安装/导入按钮 -->
            <button
              v-if="marketplaceTab === 'plugins'"
              class="flex-shrink-0 rounded-xl px-3 py-1.5 text-xs font-medium transition-colors"
              :disabled="installingId === item.id || item.installed"
              :class="installingId === item.id
                ? 'bg-neutral-100 dark:bg-neutral-700/50 text-neutral-400 cursor-wait'
                : item.hasUpdate
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                  : item.installed
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 cursor-default'
                    : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'"
              @click="installPlugin(item)"
            >
              <span v-if="installingId === item.id" class="flex items-center gap-1"><span class="i-svg-spinners:ring-resize text-xs" />{{ t('settings.dialogs.onboarding.marketplace.installing') }}</span>
              <span v-else-if="item.hasUpdate">{{ t('settings.dialogs.onboarding.marketplace.update') }}</span>
              <span v-else-if="item.installed" class="flex items-center gap-0.5"><span class="i-solar:check-circle-linear text-xs" />{{ t('settings.dialogs.onboarding.marketplace.installed') }}</span>
              <span v-else>{{ t('settings.dialogs.onboarding.marketplace.install') }}</span>
            </button>
            <button
              v-else
              class="flex-shrink-0 rounded-xl px-3 py-1.5 text-xs font-medium transition-colors"
              :disabled="installingId === item.id || !!importedPersonaIdMap[item.id]"
              :class="installingId === item.id
                ? 'bg-neutral-100 dark:bg-neutral-700/50 text-neutral-400 cursor-wait'
                : importedPersonaIdMap[item.id]
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 cursor-default'
                  : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'"
              @click="installPersona(item)"
            >
              <span v-if="installingId === item.id" class="flex items-center gap-1"><span class="i-svg-spinners:ring-resize text-xs" />{{ t('settings.dialogs.onboarding.marketplace.importing') }}</span>
              <span v-else-if="importedPersonaIdMap[item.id]" class="flex items-center gap-0.5"><span class="i-solar:check-circle-linear text-xs" />{{ t('settings.dialogs.onboarding.marketplace.imported') }}</span>
              <span v-else>{{ t('settings.dialogs.onboarding.marketplace.import') }}</span>
            </button>
          </div>

          <!-- 加载更多 -->
          <div v-if="currentList.length > 0" class="flex justify-center py-2">
            <button
              v-if="hasMore"
              class="rounded-xl px-4 py-1.5 text-xs transition-colors"
              :disabled="loadMoreLoading"
              :class="loadMoreLoading
                ? 'bg-neutral-100 dark:bg-neutral-700/50 text-neutral-400 cursor-wait'
                : 'bg-neutral-100 dark:bg-neutral-700/50 text-neutral-600 dark:text-neutral-300'"
              @click="loadMore"
            >
              <span v-if="loadMoreLoading" class="flex items-center gap-1"><span class="i-svg-spinners:ring-resize text-xs" />{{ t('settings.dialogs.onboarding.marketplace.loadingMore') }}</span>
              <span v-else>{{ t('settings.dialogs.onboarding.marketplace.loadMore') }}</span>
            </button>
            <p v-else class="text-xs text-neutral-400">
              {{ t('settings.dialogs.onboarding.marketplace.noMore') }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-motion
        :initial="{ opacity: 0, scale: 0.95 }"
        :enter="{ opacity: 1, scale: 1 }"
        :duration="350"
        :delay="400"
        class="flex gap-2"
      >
        <button
          class="flex-shrink-0 px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
          @click="emit('skip')"
        >
          {{ t('settings.dialogs.onboarding.skipStep') }}
        </button>
        <Button
          class="flex-1"
          variant="primary"
          :label="t('settings.dialogs.onboarding.next')"
          @click="emit('next')"
        />
      </div>
    </template>
  </div>
</template>
